import {sum, uniq} from 'lodash';
import {useMemo} from 'react';

import {useDeepMemo} from '../../../state/hooks';
import {CustomRunNames} from '../../../state/views/customRunNames/types';
import {RunSetPart} from '../../../state/views/runSet/types';
import * as Filter from '../../../util/filters';
import * as FilterTypes from '../../../util/filterTypes';
import {addSearchFilter, sortToOrderString} from '../../../util/queryts';
import * as QueryTypes from '../../../util/queryTypes';
import * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import * as RunTypes from '../../../util/runTypes';
import * as SelectionManager from '../../../util/selectionmanager';
import {RUN_TAGS_COL} from '../../../util/tablecols';
import {RunSelectorRunsPartialVars} from '../types';

type TopLevelQueryVariablesArgs = {
  customRunNames?: CustomRunNames;
  enableArtifactCounts: boolean;
  entityName: string;
  groupSelections: SelectionManager.GroupSelectionState;
  isTableCollapsed: boolean;
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>;
  projectName: string;
  runSet: NonNullable<RunSetPart>;
  runSetFilters: FilterTypes.RootFilter<RunTypes.Key>;
  sort: QueryTypes.Sort;
  tableSettings: RunFeed.Config;
};

export const useTopLevelQueryVariables = ({
  customRunNames,
  enableArtifactCounts,
  entityName,
  groupSelections,
  isTableCollapsed,
  mergeFilters,
  projectName,
  runSet,
  runSetFilters,
  sort,
  tableSettings,
}: TopLevelQueryVariablesArgs): RunSelectorRunsPartialVars => {
  const {grouping} = groupSelections;
  const {columnOrder, columnPinned} = tableSettings;

  const filters = useMemo(() => {
    const mergedFilters =
      mergeFilters != null
        ? Filter.mergedFilters(runSetFilters, mergeFilters)
        : Filter.mergedFilters(runSetFilters);
    const filtersWithSearch = addSearchFilter(
      mergedFilters,
      runSet.search,
      grouping,
      customRunNames
    );
    return filtersWithSearch;
  }, [customRunNames, grouping, mergeFilters, runSet.search, runSetFilters]);

  const selections = useMemo(
    () => SelectionManager.toRunFilter(groupSelections),
    [groupSelections]
  );

  let queryColumnOrder = columnOrder;
  if (isTableCollapsed) {
    // If the table is collapsed, only fetch the pinned columns.
    const numPinnedColumns = sum(Object.values(columnPinned));
    queryColumnOrder = columnOrder.slice(0, numPinnedColumns);
  }

  const fetchColumns: RunTypes.Key[] = useMemo(
    () => [
      ...grouping,
      ...(queryColumnOrder
        .map(Run.keyFromString)
        .filter(key => !!key) as RunTypes.Key[]),
      ...(selections != null
        ? Filter.getUniqueKeysUsedInFilter(selections)
        : []),
    ],
    [grouping, queryColumnOrder, selections]
  );

  const topLevelQueryVariables = useDeepMemo(
    useMemo(() => {
      const configKeys = fetchColumns
        .filter(key => key.section === 'config')
        .map(key => key.name);
      const summaryKeys = fetchColumns
        .filter(key => key.section === 'summary')
        .map(key => key.name);
      const aggregationKeys = uniq(
        fetchColumns
          .filter(
            key =>
              key.section === 'aggregations_min' ||
              key.section === 'aggregations_max'
          )
          .map(key => key.name)
      );
      const enableTags = fetchColumns.some(
        key => Run.keyToString(key) === RUN_TAGS_COL
      );

      return {
        aggregationKeys,
        configKeys,
        enableAggregations: aggregationKeys.length > 0,
        enableArtifactCounts,
        enableConfig: configKeys.length > 0,
        enableSummary: summaryKeys.length > 0,
        enableTags,
        entityName,
        filters,
        grouping,
        groupLevel: 0,
        order: sortToOrderString(sort),
        projectName,
        selections,
        summaryKeys,
        wandbKeys: ['cli_version', 'is_jupyter_run'],
      };
    }, [
      enableArtifactCounts,
      entityName,
      fetchColumns,
      filters,
      grouping,
      projectName,
      selections,
      sort,
    ])
  );

  return topLevelQueryVariables;
};
